import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { adminGet } from "../../lib/adminApi";
import { STATUS_LABEL } from "../../components/admin/AdminUI";
import { Badge, Card, InkPanel, MetricCard, PageHeader, Progress, StatBlock, StatusBadge } from "../../components/fulkra";

/**
 * Dashboard in de huisstijl.
 *
 * Restyling, geen herbouw: alle bevragingen, drempels, berekeningen, secties en
 * data-testid's zijn ongewijzigd. Wat verandert is de vormgeving — kaarten in plaats van
 * gekleurde vlakken, turkoois als enige actiekleur, en de betekeniskleuren alleen daar
 * waar ze iets betekenen (rood bij pijn-signalen en mislukte notificaties, oranje bij
 * werk dat blijft liggen).
 *
 * De cijferband op inkt staat er zoals het ontwerp hem tekent, maar met echte getallen:
 * actieve deelnemers, deelnemers met zolen, open signalen. Staat er niets in de database,
 * dan staat er 0 — dat is een antwoord, leeg is dat niet.
 *
 * Twee dingen uit het prototype staan er bewust NIET in: het cijfer bij "verzuim
 * gemiddeld" (de app meet geen verzuim; daar staat een streepje met "nog niet gemeten")
 * en de verlooplijntjes onder de tegels (die vragen een reeks over de tijd, en die
 * bestaat nog niet). Cijfers verzinnen mag niet — harde eis 5 uit de bouwopdracht.
 */

/* De API weigert een limiet boven 100 (Query(le=100) in de routers). Stond hier
   200 respectievelijk 500, dan kwam er een 422 terug en bleef het hele dashboard
   leeg — zonder zichtbare foutmelding. */
const LIJST_LIMIET = 100;

const CLUSTER_DREMPEL_PERCENT = 40;
const CLUSTER_DREMPEL_WERKNEMERS = 25;
const FUNNEL_VOLGORDE = ["aangemeld", "baseline_voltooid", "aangemeten", "besteld", "geleverd", "week4_voltooid", "week12_voltooid", "afgerond"];

function todayIso() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().slice(0, 10);
}
function plus7DaysIso() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}
function sevenDaysAgoIso() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}

/** Kopje boven een sectie: kleinkapitaal met letterafstand, zoals STIJL.md voorschrijft. */
function SectieKop({ children }) {
  return (
    <h2
      style={{
        fontFamily: "var(--font-tekst)", fontWeight: "var(--fw-semi)", fontSize: "var(--fs-xs)",
        lineHeight: 1.55, letterSpacing: "var(--ls-label)", textTransform: "uppercase",
        color: "var(--text-soft)", marginBottom: "var(--ruimte-5)",
      }}
    >
      {children}
    </h2>
  );
}

/** Tegel met een kerncijfer. De Link eromheen houdt het navigeren binnen de app;
    MetricCard's eigen href zou de hele pagina opnieuw laden. */
function Tegel({ label, waarde, href, tone, loading, testid, note }) {
  /* De wikkel moet meerekken in het raster, anders worden de tegels ongelijk hoog
     zodra één label over twee regels loopt. Vandaar display:grid op beide lagen. */
  const kaart = (
    <div data-testid={testid} style={{ display: "grid", height: "100%" }}>
      <MetricCard label={label} value={loading ? "…" : waarde} tone={tone} note={note} />
    </div>
  );
  return href ? (
    <Link to={href} style={{ textDecoration: "none", display: "grid", height: "100%" }}>{kaart}</Link>
  ) : kaart;
}

export default function DashboardPage() {
  const [filterWerkgeverId, setFilterWerkgeverId] = useState("");

  const werkgeversQ = useQuery({
    queryKey: ["werkgevers", "all-dashboard"],
    queryFn: () => adminGet("/api/admin/werkgevers", { limit: LIJST_LIMIET }),
  });

  const flagsParams = { afgehandeld: false, limit: LIJST_LIMIET };
  if (filterWerkgeverId) flagsParams.werkgever_id = filterWerkgeverId;
  const pijnQ = useQuery({
    queryKey: ["dash", "pijn-open", filterWerkgeverId],
    queryFn: () => adminGet("/api/admin/pijn-flags", flagsParams),
    refetchInterval: 60000,
  });

  const aanmeetParams = { datum_from: todayIso(), datum_to: plus7DaysIso(), limit: 100 };
  if (filterWerkgeverId) aanmeetParams.werkgever_id = filterWerkgeverId;
  const aanmeetQ = useQuery({
    queryKey: ["dash", "aanmeet-7d", filterWerkgeverId],
    queryFn: () => adminGet("/api/admin/aanmeetdagen", aanmeetParams),
  });

  const bestelParams = { status: "besteld", limit: 100 };
  if (filterWerkgeverId) bestelParams.werkgever_id = filterWerkgeverId;
  const bestelQ = useQuery({
    queryKey: ["dash", "bestel-open", filterWerkgeverId],
    queryFn: () => adminGet("/api/admin/bestellingen", bestelParams),
  });

  const notifParams = { status: "gefaald", van: sevenDaysAgoIso() + "T00:00:00", limit: 100 };
  if (filterWerkgeverId) notifParams.werkgever_id = filterWerkgeverId;
  const notifQ = useQuery({
    queryKey: ["dash", "notif-failed-7d", filterWerkgeverId],
    queryFn: () => adminGet("/api/admin/notificaties", notifParams),
  });

  const noShowParams = { status: "geannuleerd", datum_van: sevenDaysAgoIso(), limit: 100 };
  if (filterWerkgeverId) noShowParams.werkgever_id = filterWerkgeverId;
  const noShowQ = useQuery({
    queryKey: ["dash", "no-shows-7d", filterWerkgeverId],
    queryFn: () => adminGet("/api/admin/aanmeetdagen", noShowParams),
  });

  const werknemersParams = { limit: LIJST_LIMIET };
  if (filterWerkgeverId) werknemersParams.werkgever_id = filterWerkgeverId;
  const werknemersQ = useQuery({
    queryKey: ["dash", "werknemers", filterWerkgeverId],
    queryFn: () => adminGet("/api/admin/werknemers", werknemersParams),
  });

  const pijnItems = pijnQ.data?.items || [];
  const aanmeetItems = aanmeetQ.data?.items || [];
  const bestelItems = bestelQ.data?.items || [];
  const notifItems = notifQ.data?.items || [];
  const werknemers = werknemersQ.data?.items || [];

  const pijnTotaal = pijnQ.data?.total ?? pijnItems.length;
  const aanmeetTotaal = aanmeetItems.length;
  const bestelTotaal = bestelQ.data?.total ?? bestelItems.length;
  const notifTotaal = notifQ.data?.total ?? notifItems.length;
  const noShowTotaal = noShowQ.data?.total ?? (noShowQ.data?.items?.length || 0);

  /* Cijferband: vier getallen uit de echte gegevens. Waar niets is, staat 0 — dat is
     een antwoord, leeg is dat niet. "Verzuim gemiddeld" staat er als streepje: de app
     meet geen verzuim, en een cijfer verzinnen mag niet (harde eis 5). */
  const AFGEROND_OF_GESTOPT = ["afgerond", "opgezegd", "geanonymiseerd", "gepauzeerd"];
  const deelnemersActief = werknemers.filter((w) => !AFGEROND_OF_GESTOPT.includes(w.status)).length;
  const werkgeversMetDeelnemers = new Set(werknemers.map((w) => w.werkgever_naam || "Onbekend")).size;
  const zolenGeleverd = werknemers.filter((w) => ["geleverd", "week4_voltooid", "week12_voltooid", "afgerond"].includes(w.status)).length;
  const directeSignalen = pijnItems.filter((f) => !f.afgehandeld_op && f.notificatie_categorie === "direct").length;

  const werkgeverClusters = useMemo(() => {
    if (!pijnItems.length || !werknemers.length) return [];
    const flagsPerWg = new Map();
    for (const f of pijnItems) {
      const key = f.werkgever_naam || "Onbekend";
      flagsPerWg.set(key, (flagsPerWg.get(key) || 0) + 1);
    }
    const werknemersPerWg = new Map();
    const flaggedWerknemers = new Map();
    for (const w of werknemers) {
      const key = w.werkgever_naam || "Onbekend";
      werknemersPerWg.set(key, (werknemersPerWg.get(key) || 0) + 1);
    }
    for (const f of pijnItems) {
      const key = f.werkgever_naam || "Onbekend";
      if (!flaggedWerknemers.has(key)) flaggedWerknemers.set(key, new Set());
      flaggedWerknemers.get(key).add(f.werknemer_id);
    }
    const totalFlags = pijnItems.length;
    const clusters = [];
    for (const [naam, aantal] of flagsPerWg.entries()) {
      const percentVanTotaal = totalFlags ? (aantal / totalFlags) * 100 : 0;
      const werknemers_in_wg = werknemersPerWg.get(naam) || 0;
      const werknemers_met_flag = flaggedWerknemers.get(naam)?.size || 0;
      const percentMedewerkers = werknemers_in_wg ? (werknemers_met_flag / werknemers_in_wg) * 100 : 0;
      const isCluster = percentVanTotaal >= CLUSTER_DREMPEL_PERCENT || percentMedewerkers >= CLUSTER_DREMPEL_WERKNEMERS;
      if (isCluster) clusters.push({ naam, aantal, percentVanTotaal, werknemers_in_wg, werknemers_met_flag, percentMedewerkers });
    }
    return clusters.sort((a, b) => b.aantal - a.aantal);
  }, [pijnItems, werknemers]);

  const funnel = useMemo(() => {
    const counts = new Map();
    for (const w of werknemers) counts.set(w.status, (counts.get(w.status) || 0) + 1);
    const total = werknemers.length;
    return FUNNEL_VOLGORDE.map((s) => ({
      status: s,
      count: counts.get(s) || 0,
      percent: total ? ((counts.get(s) || 0) / total) * 100 : 0,
    }));
  }, [werknemers]);

  const aandachtWerknemers = useMemo(() => {
    const now = Date.now();
    const result = [];
    for (const w of werknemers) {
      const aanmelddatum = w.aanmelddatum ? new Date(w.aanmelddatum).getTime() : null;
      const daysSinceAanmeld = aanmelddatum ? (now - aanmelddatum) / (1000 * 60 * 60 * 24) : 0;
      let reden = null;
      if (w.status === "aangemeld" && daysSinceAanmeld > 14) reden = `Geen baseline na ${Math.floor(daysSinceAanmeld)} dagen`;
      else if (w.status === "baseline_voltooid" && daysSinceAanmeld > 21) reden = `Baseline klaar maar nog niet aangemeten (${Math.floor(daysSinceAanmeld)} dagen)`;
      else if (w.status === "geleverd" && daysSinceAanmeld > 60) reden = `Zolen geleverd, geen wencontrole (${Math.floor(daysSinceAanmeld)} dagen sinds aanmelding)`;
      else if (w.status === "week4_voltooid" && daysSinceAanmeld > 100) reden = `Wencontrole klaar, geen vervolgmeting (${Math.floor(daysSinceAanmeld)} dagen sinds aanmelding)`;
      if (reden) result.push({ ...w, reden });
    }
    return result.slice(0, 5);
  }, [werknemers]);

  // Engine-aandacht: top regels + directe escalaties uit pijnItems (open)
  const engineTopRegels = useMemo(() => {
    const counts = new Map();
    for (const f of pijnItems) {
      if (f.afgehandeld_op) continue;
      const key = f.regel_id || "(legacy)";
      const cur = counts.get(key) || { regel_id: key, aantal: 0, omschrijving: f.omschrijving || "" };
      cur.aantal += 1;
      if (!cur.omschrijving && f.omschrijving) cur.omschrijving = f.omschrijving;
      counts.set(key, cur);
    }
    return Array.from(counts.values()).sort((a, b) => b.aantal - a.aantal).slice(0, 5);
  }, [pijnItems]);

  const engineDirect = useMemo(() => {
    return pijnItems
      .filter((f) => !f.afgehandeld_op && f.notificatie_categorie === "direct")
      .slice(0, 5);
  }, [pijnItems]);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        size="display"
        subtitle="Operationeel overzicht van het programma"
        actions={
          <div style={{ width: 240 }}>
            <select
              value={filterWerkgeverId}
              onChange={(e) => setFilterWerkgeverId(e.target.value)}
              aria-label="Filter op werkgever"
              data-testid="dash-werkgever-filter"
              style={{
                width: "100%", minHeight: "var(--veld-hoogte-desktop)",
                borderRadius: "var(--radius)", border: "1px solid var(--border-subtle)",
                background: "var(--wit)", color: "var(--text-strong)",
                padding: "0 var(--ruimte-5)", fontSize: "var(--fs-body)", fontFamily: "var(--font-tekst)",
                boxShadow: "var(--schaduw-1)",
              }}
            >
              <option value="">Alle werkgevers</option>
              {(werkgeversQ.data?.items || []).map((w) => (
                <option key={w.werkgever_id} value={w.werkgever_id}>{w.bedrijfsnaam}</option>
              ))}
            </select>
          </div>
        }
      />

      <InkPanel style={{ marginBottom: "var(--ruimte-9)" }} data-testid="dash-cijferband">
        <div style={{ display: "grid", gap: "var(--ruimte-8)", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}>
          <StatBlock
            tone="donker" size="middel" label="Deelnemers actief"
            value={werknemersQ.isLoading ? "…" : deelnemersActief}
            note={`over ${werkgeversMetDeelnemers} werkgever${werkgeversMetDeelnemers === 1 ? "" : "s"}`}
          />
          <StatBlock
            tone="donker" size="middel" label="Zolen geleverd"
            value={werknemersQ.isLoading ? "…" : zolenGeleverd}
            note="deelnemers met zolen"
          />
          <StatBlock
            tone="donker" size="middel" label="Verzuim gemiddeld"
            value="—"
            note="nog niet gemeten"
          />
          <StatBlock
            tone="donker" size="middel" label="Open signalen"
            value={pijnQ.isLoading ? "…" : pijnTotaal}
            note={`waarvan ${directeSignalen} direct`}
          />
        </div>
      </InkPanel>

      <section style={{ marginBottom: "var(--ruimte-9)" }} data-testid="dash-sectie-vandaag">
        <SectieKop>Vandaag te doen</SectieKop>
        <div style={{ display: "grid", gap: "var(--ruimte-5)", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          <Tegel label="Open pijn-signalen" waarde={pijnTotaal} tone="alarm" href="/admin/pijn-flags?status=open" loading={pijnQ.isLoading} testid="card-pijn-open" />
          <Tegel label="Aanmeetdagen 7 dagen" waarde={aanmeetTotaal} tone="actie" href="/admin/aanmeetdagen" loading={aanmeetQ.isLoading} testid="card-aanmeet-7d" />
          <Tegel label="No-shows (7d)" waarde={noShowTotaal} tone="aandacht" href="/admin/aanmeetdagen" loading={noShowQ.isLoading} testid="card-no-shows" />
          <Tegel label="Open bestellingen" waarde={bestelTotaal} tone="aandacht" href="/admin/bestellingen?status=besteld" loading={bestelQ.isLoading} testid="card-bestel-open" />
          <Tegel label="Mislukte notificaties" waarde={notifTotaal} tone={notifTotaal > 0 ? "alarm" : "neutraal"} href="/admin/notificaties?status=gefaald" loading={notifQ.isLoading} testid="card-notif-failed" />
        </div>
      </section>

      {werkgeverClusters.length > 0 ? (
        <section style={{ marginBottom: "var(--ruimte-9)" }} data-testid="dash-sectie-clusters">
          <SectieKop>Werkgevers met cluster-signaal</SectieKop>
          <div
            style={{
              background: "var(--aandacht-zacht)", border: "1px solid var(--aandacht-rand)",
              borderRadius: "var(--radius-vlak)", padding: "var(--ruimte-6)",
            }}
          >
            <p style={{ marginBottom: "var(--ruimte-5)", fontSize: "var(--fs-body)", color: "var(--text-aandacht)" }}>
              Bedrijven waar onevenredig veel pijn-signalen of werknemer-klachten vandaan komen — overweeg extra contact.
              Drempel: ≥{CLUSTER_DREMPEL_PERCENT}% van alle signalen óf ≥{CLUSTER_DREMPEL_WERKNEMERS}% werknemers met signaal.
            </p>
            <ul style={{ display: "grid", gap: "var(--ruimte-4)", listStyle: "none", margin: 0, padding: 0 }}>
              {werkgeverClusters.map((c) => (
                <li
                  key={c.naam}
                  data-testid={`cluster-${c.naam}`}
                  style={{
                    display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between",
                    gap: "var(--ruimte-4)", background: "var(--wit)", borderRadius: "var(--radius)",
                    padding: "var(--ruimte-4) var(--ruimte-5)", boxShadow: "var(--elevation-card)",
                  }}
                >
                  <div>
                    <div style={{ fontSize: "var(--fs-body)", fontWeight: "var(--fw-medium)", color: "var(--text-strong)" }}>{c.naam}</div>
                    <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
                      {c.aantal} open signaal{c.aantal === 1 ? "" : "en"}
                      {c.werknemers_in_wg > 0 ? ` · ${c.werknemers_met_flag}/${c.werknemers_in_wg} werknemers` : null}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "var(--ruimte-4)" }}>
                    <span
                      style={{
                        borderRadius: "var(--radius)", padding: "2px var(--ruimte-4)", fontSize: "var(--fs-xs)",
                        fontWeight: "var(--fw-medium)", background: "var(--aandacht-zacht)",
                        border: "1px solid var(--aandacht-rand)", color: "var(--text-aandacht)",
                      }}
                    >
                      {Math.round(c.percentVanTotaal)}% van alle signalen
                    </span>
                    {c.percentMedewerkers >= CLUSTER_DREMPEL_WERKNEMERS ? (
                      <span
                        style={{
                          borderRadius: "var(--radius)", padding: "2px var(--ruimte-4)", fontSize: "var(--fs-xs)",
                          fontWeight: "var(--fw-medium)", background: "var(--alarm-zacht)",
                          border: "1px solid var(--alarm-rand)", color: "var(--text-alarm)",
                        }}
                      >
                        {Math.round(c.percentMedewerkers)}% medewerkers
                      </span>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      <section style={{ marginBottom: "var(--ruimte-9)" }} data-testid="dash-sectie-engine">
          <SectieKop>Signaal-engine aandacht</SectieKop>
          <div className="fulkra-engine-raster" style={{ display: "grid", gap: "var(--ruimte-6)" }}>
            <Card title="Top-regels open" subtitle="Automatisch gemeld bij een pijncijfer van 7 of hoger">
              {engineTopRegels.length === 0 ? (
                <p style={{ fontSize: "var(--fs-sm)", color: "var(--text-soft)" }}>Geen open signalen.</p>
              ) : (
                <ul style={{ display: "grid", gap: "var(--ruimte-5)", listStyle: "none", margin: 0, padding: 0 }}>
                  {engineTopRegels.map((r) => (
                    <li key={r.regel_id} style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "var(--ruimte-4)", fontSize: "var(--fs-sm)" }}>
                      <span>
                        <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-body)" }}>{r.regel_id}</span>
                        {r.omschrijving && <span style={{ color: "var(--text-soft)" }}> — {r.omschrijving}</span>}
                      </span>
                      <Badge>{r.aantal}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card
              title="Directe escalaties"
              style={{ borderColor: "var(--alarm-rand)", background: "var(--alarm-zacht)" }}
            >
              <div style={{ display: "grid", gap: "var(--ruimte-4)", fontSize: "var(--fs-sm)" }}>
                {engineDirect.length === 0 ? (
                  <p style={{ color: "var(--text-alarm)" }}>Geen directe escalaties op dit moment.</p>
                ) : (
                  engineDirect.map((f) => (
                    <Link
                      key={f.flag_id}
                      to={`/admin/pijn-flags?werknemer_id=${f.werknemer_id}`}
                      style={{ color: "var(--text-alarm)" }}
                    >
                      {f.klantnummer || "?"} — {f.regel_id || "(legacy)"}
                    </Link>
                  ))
                )}
                <p style={{ color: "var(--text-alarm)", fontSize: "var(--fs-xs)" }}>
                  Direct doorgezet naar de podotherapeut per mail.
                </p>
              </div>
            </Card>
          </div>
      </section>

      {aandachtWerknemers.length > 0 ? (
        <section style={{ marginBottom: "var(--ruimte-9)" }} data-testid="dash-sectie-aandacht">
          <SectieKop>Werknemers met aandacht</SectieKop>
          <div
            style={{
              overflow: "hidden", background: "var(--wit)", border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-vlak)", boxShadow: "var(--elevation-card)",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--fs-body)" }}>
              <thead style={{ background: "var(--surface-sunken)" }}>
                <tr>
                  {["Klantnr.", "Naam", "Werkgever", "Status", "Aandacht omdat"].map((k) => (
                    <th
                      key={k}
                      scope="col"
                      style={{
                        textAlign: "left", padding: "var(--ruimte-4) var(--ruimte-5)",
                        fontSize: "var(--fs-xs)", letterSpacing: "var(--ls-label)", textTransform: "uppercase",
                        fontWeight: "var(--fw-medium)", color: "var(--text-soft)",
                        borderBottom: "1px solid var(--border-subtle)",
                      }}
                    >
                      {k}
                    </th>
                  ))}
                  <th
                    scope="col"
                    style={{
                      textAlign: "right", padding: "var(--ruimte-4) var(--ruimte-5)",
                      fontSize: "var(--fs-xs)", letterSpacing: "var(--ls-label)", textTransform: "uppercase",
                      fontWeight: "var(--fw-medium)", color: "var(--text-soft)",
                      borderBottom: "1px solid var(--border-subtle)",
                    }}
                  >
                    Actie
                  </th>
                </tr>
              </thead>
              <tbody>
                {aandachtWerknemers.map((w) => (
                  <tr key={w.werknemer_id} style={{ borderTop: "1px solid var(--neutraal-100)" }}>
                    <td style={{ padding: "var(--ruimte-4) var(--ruimte-5)", fontFamily: "var(--font-mono)", fontSize: "var(--fs-sm)" }}>{w.klantnummer}</td>
                    <td style={{ padding: "var(--ruimte-4) var(--ruimte-5)", color: "var(--text-strong)" }}>{[w.voorletters, w.achternaam].filter(Boolean).join(" ")}</td>
                    <td style={{ padding: "var(--ruimte-4) var(--ruimte-5)", color: "var(--text-soft)" }}>{w.werkgever_naam || "—"}</td>
                    <td style={{ padding: "var(--ruimte-4) var(--ruimte-5)" }}><StatusBadge value={w.status} /></td>
                    <td style={{ padding: "var(--ruimte-4) var(--ruimte-5)", fontSize: "var(--fs-sm)", color: "var(--text-alarm)" }}>{w.reden}</td>
                    <td style={{ padding: "var(--ruimte-4) var(--ruimte-5)", textAlign: "right" }}>
                      <Link to={`/admin/werknemers/${w.werknemer_id}`} style={{ fontSize: "var(--fs-sm)", fontWeight: "var(--fw-medium)", color: "var(--text-action)" }}>
                        Bekijken →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <section style={{ marginBottom: "var(--ruimte-9)" }} data-testid="dash-sectie-funnel">
        <SectieKop>Pipeline — werknemers per fase</SectieKop>
        {werknemersQ.isLoading ? (
          <Card><span style={{ color: "var(--text-soft)" }}>Laden…</span></Card>
        ) : (
          <Card>
            <div style={{ display: "grid", gap: "var(--ruimte-5)" }}>
              {funnel.map((f) => (
                <div
                  key={f.status}
                  style={{ display: "grid", gridTemplateColumns: "230px 1fr 56px", alignItems: "center", gap: "var(--ruimte-6)" }}
                >
                  <span style={{ fontSize: "var(--fs-sm)", color: "var(--text-body)" }}>
                    {STATUS_LABEL[f.status] || f.status}
                  </span>
                  <Progress value={f.percent} height={22} inlineLabel={`${Math.round(f.percent)}%`} />
                  <span style={{ textAlign: "right", fontSize: "var(--fs-sm)", fontWeight: "var(--fw-semi)", fontVariantNumeric: "tabular-nums", color: "var(--text-body)" }}>
                    {f.count}
                  </span>
                </div>
              ))}
            </div>
            <p style={{ marginTop: "var(--ruimte-6)", fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
              Totaal: {werknemers.length} werknemers{filterWerkgeverId ? " (gefilterd)" : ""}. Vernieuwt automatisch elke minuut.
            </p>
          </Card>
        )}
      </section>

    </div>
  );
}
